import { client, handleRequest, handleRequestBody } from "./client.ts";

export type RdoStatus =
	| "draft"
	| "generating"
	| "finalized"
	| "failed"
	| "superseded";

export interface RdoPhoto {
	id: string;
	position: number;
	source_post_media_id?: string;
	url: string;
	width: number | null;
	height: number | null;
	caption: string;
}

export interface RdoActivity {
	id: string;
	position: number;
	title: string;
	stage_id?: string;
	status?: string;
	occurred_at: string;
	description: string;
	source_post_id?: string;
	photos: RdoPhoto[];
}

export interface RdoSignatures {
	diary: string;
	technical: string;
	contractor: string;
}

export interface RdoCurvePoint {
	period: string;
	label: string;
	planned: number;
	actual: number | null;
}

export interface RdoProductionPeriod {
	period: string;
	label: string;
	start: string;
	end: string;
	gain: number;
}

export interface RdoPerformance {
	planned: number;
	actual: number;
	deviation: number;
	completed_count: number;
	curve: RdoCurvePoint[];
	production: RdoProductionPeriod[];
	interval: string;
	no_baseline: boolean;
}

export type RdoPhotoSize = "small" | "medium" | "large";

export interface RdoScheduledService {
	id: string;
	position: number;
	description: string;
	stage_id?: string;
}

export interface RdoSummary {
	id: string;
	sequence_number: number;
	report_date: string;
	revision: number;
	status: RdoStatus;
	technical_manager: string;
	weather_condition: string | null;
	first_record_at: string | null;
	last_record_at: string | null;
	in_progress_count: number;
	completed_count: number;
	observations: string;
	signatures: RdoSignatures;
	crew_annex_enabled: boolean;
	performance_enabled: boolean;
	photo_size: RdoPhotoSize;
	performance?: RdoPerformance;
	pdf_url?: string;
	failure_reason?: string;
	revised_from_id?: string;
	superseded_by_id?: string;
	finalized_at: string | null;
	created_at: string;
	updated_at: string;
}

export interface Rdo extends RdoSummary {
	activities: RdoActivity[];
	scheduled_services: RdoScheduledService[];
}

export interface ListRdosParams {
	from?: string;
	to?: string;
	status?: RdoStatus;
	includeSuperseded?: boolean;
	page?: number;
	pageSize?: number;
}

export interface ListRdosResponse {
	count: number;
	items: RdoSummary[];
}

interface RdoResponse {
	rdo: Rdo;
}

interface RdoActivityResponse {
	activity: RdoActivity;
}

interface RdoActivitiesResponse {
	items: RdoActivity[];
}

interface RdoScheduledServiceResponse {
	scheduled_service: RdoScheduledService;
}

interface RdoScheduledServicesResponse {
	items: RdoScheduledService[];
}

interface RdoPhotoResponse {
	photo: RdoPhoto;
}

function rdosPath(constructionId: string, suffix = ""): string {
	return `/constructions/${constructionId}/rdos${suffix}`;
}

export function listRdos(
	constructionId: string,
	params: ListRdosParams = {},
): Promise<ListRdosResponse> {
	return handleRequestBody(() =>
		client.get<ListRdosResponse>(rdosPath(constructionId), {
			params: {
				from: params.from,
				to: params.to,
				status: params.status,
				include_superseded: params.includeSuperseded ? true : undefined,
				page: params.page,
				page_size: params.pageSize,
			},
		}),
	);
}

export interface CreateRdoInput {
	reportDate: string;
}

export async function createRdo(
	constructionId: string,
	input: CreateRdoInput,
): Promise<Rdo> {
	const { rdo } = await handleRequestBody(() =>
		client.post<RdoResponse>(rdosPath(constructionId), {
			report_date: input.reportDate,
		}),
	);
	return rdo;
}

export async function getRdo(
	constructionId: string,
	rdoId: string,
): Promise<Rdo> {
	const { rdo } = await handleRequestBody(() =>
		client.get<RdoResponse>(rdosPath(constructionId, `/${rdoId}`)),
	);
	return rdo;
}

export interface UpdateRdoInput {
	technicalManager?: string;
	weatherCondition?: string;
	firstRecordAt?: string;
	lastRecordAt?: string;
	observations?: string;
	signatures?: Partial<RdoSignatures>;
	crewAnnexEnabled?: boolean;
	performanceEnabled?: boolean;
	photoSize?: RdoPhotoSize;
}

export async function updateRdo(
	constructionId: string,
	rdoId: string,
	input: UpdateRdoInput,
): Promise<Rdo> {
	const { rdo } = await handleRequestBody(() =>
		client.patch<RdoResponse>(rdosPath(constructionId, `/${rdoId}`), {
			technical_manager: input.technicalManager,
			weather_condition: input.weatherCondition,
			first_record_at: input.firstRecordAt,
			last_record_at: input.lastRecordAt,
			observations: input.observations,
			signatures: input.signatures,
			crew_annex_enabled: input.crewAnnexEnabled,
			performance_enabled: input.performanceEnabled,
			photo_size: input.photoSize,
		}),
	);
	return rdo;
}

export async function deleteRdo(
	constructionId: string,
	rdoId: string,
): Promise<void> {
	await handleRequest(() =>
		client.delete(rdosPath(constructionId, `/${rdoId}`)),
	);
}

export interface RdoActivityInput {
	title: string;
	stageId?: string;
	status?: string;
	occurredAt: string;
	description: string;
}

function activityBody(input: RdoActivityInput) {
	return {
		title: input.title,
		stage_id: input.stageId || null,
		status: input.status || null,
		occurred_at: input.occurredAt,
		description: input.description,
	};
}

export async function createRdoActivity(
	constructionId: string,
	rdoId: string,
	input: RdoActivityInput,
): Promise<RdoActivity> {
	const { activity } = await handleRequestBody(() =>
		client.post<RdoActivityResponse>(
			rdosPath(constructionId, `/${rdoId}/activities`),
			activityBody(input),
		),
	);
	return activity;
}

export async function updateRdoActivity(
	constructionId: string,
	rdoId: string,
	activityId: string,
	input: RdoActivityInput,
): Promise<RdoActivity> {
	const { activity } = await handleRequestBody(() =>
		client.patch<RdoActivityResponse>(
			rdosPath(constructionId, `/${rdoId}/activities/${activityId}`),
			activityBody(input),
		),
	);
	return activity;
}

export async function deleteRdoActivity(
	constructionId: string,
	rdoId: string,
	activityId: string,
): Promise<void> {
	await handleRequest(() =>
		client.delete(
			rdosPath(constructionId, `/${rdoId}/activities/${activityId}`),
		),
	);
}

export async function reorderRdoActivities(
	constructionId: string,
	rdoId: string,
	ids: string[],
): Promise<RdoActivity[]> {
	const { items } = await handleRequestBody(() =>
		client.put<RdoActivitiesResponse>(
			rdosPath(constructionId, `/${rdoId}/activities/order`),
			{ ids },
		),
	);
	return items;
}

export interface CreateRdoScheduledServiceInput {
	description: string;
	stageId?: string;
}

export async function createRdoScheduledService(
	constructionId: string,
	rdoId: string,
	input: CreateRdoScheduledServiceInput,
): Promise<RdoScheduledService> {
	const { scheduled_service } = await handleRequestBody(() =>
		client.post<RdoScheduledServiceResponse>(
			rdosPath(constructionId, `/${rdoId}/scheduled-services`),
			{ description: input.description, stage_id: input.stageId || null },
		),
	);
	return scheduled_service;
}

export interface UpdateRdoScheduledServiceInput {
	description?: string;
	stageId?: string | null;
}

export async function updateRdoScheduledService(
	constructionId: string,
	rdoId: string,
	serviceId: string,
	input: UpdateRdoScheduledServiceInput,
): Promise<RdoScheduledService> {
	const { scheduled_service } = await handleRequestBody(() =>
		client.patch<RdoScheduledServiceResponse>(
			rdosPath(constructionId, `/${rdoId}/scheduled-services/${serviceId}`),
			{ description: input.description, stage_id: input.stageId },
		),
	);
	return scheduled_service;
}

export async function deleteRdoScheduledService(
	constructionId: string,
	rdoId: string,
	serviceId: string,
): Promise<void> {
	await handleRequest(() =>
		client.delete(
			rdosPath(constructionId, `/${rdoId}/scheduled-services/${serviceId}`),
		),
	);
}

export async function reorderRdoScheduledServices(
	constructionId: string,
	rdoId: string,
	ids: string[],
): Promise<RdoScheduledService[]> {
	const { items } = await handleRequestBody(() =>
		client.put<RdoScheduledServicesResponse>(
			rdosPath(constructionId, `/${rdoId}/scheduled-services/order`),
			{ ids },
		),
	);
	return items;
}

export type AttachRdoPhotoInput =
	| { postMediaId: string; caption?: string }
	| { file: File; caption?: string };

export async function attachRdoPhoto(
	constructionId: string,
	rdoId: string,
	activityId: string,
	input: AttachRdoPhotoInput,
): Promise<RdoPhoto> {
	const path = rdosPath(
		constructionId,
		`/${rdoId}/activities/${activityId}/photos`,
	);
	if ("file" in input) {
		const form = new FormData();
		form.append("file", input.file);
		if (input.caption) form.append("caption", input.caption);
		const { photo } = await handleRequestBody(() =>
			client.post<RdoPhotoResponse>(path, form),
		);
		return photo;
	}
	const { photo } = await handleRequestBody(() =>
		client.post<RdoPhotoResponse>(path, {
			post_media_id: input.postMediaId,
			caption: input.caption ?? "",
		}),
	);
	return photo;
}

export async function updateRdoPhoto(
	constructionId: string,
	rdoId: string,
	photoId: string,
	caption: string,
): Promise<RdoPhoto> {
	const { photo } = await handleRequestBody(() =>
		client.patch<RdoPhotoResponse>(
			rdosPath(constructionId, `/${rdoId}/photos/${photoId}`),
			{ caption },
		),
	);
	return photo;
}

export async function detachRdoPhoto(
	constructionId: string,
	rdoId: string,
	photoId: string,
): Promise<void> {
	await handleRequest(() =>
		client.delete(rdosPath(constructionId, `/${rdoId}/photos/${photoId}`)),
	);
}

async function postRdoAction(
	constructionId: string,
	rdoId: string,
	action: string,
): Promise<Rdo> {
	const { rdo } = await handleRequestBody(() =>
		client.post<RdoResponse>(rdosPath(constructionId, `/${rdoId}/${action}`)),
	);
	return rdo;
}

export function finalizeRdo(constructionId: string, rdoId: string) {
	return postRdoAction(constructionId, rdoId, "finalize");
}

export function retryRdo(constructionId: string, rdoId: string) {
	return postRdoAction(constructionId, rdoId, "retry");
}

export function openRdoRevision(constructionId: string, rdoId: string) {
	return postRdoAction(constructionId, rdoId, "revisions");
}

export interface RdoPdfFile {
	blob: Blob;
	filename: string;
}

const FILENAME_PATTERN = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i;

export function filenameFromDisposition(
	header: string | undefined,
	fallback: string,
): string {
	const match = header?.match(FILENAME_PATTERN);
	return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

export async function downloadRdoPdf(
	constructionId: string,
	rdoId: string,
	fallbackFilename: string,
): Promise<RdoPdfFile> {
	const response = await handleRequest(() =>
		client.get<Blob>(rdosPath(constructionId, `/${rdoId}/pdf`), {
			responseType: "blob",
		}),
	);
	return {
		blob: response.data,
		filename: filenameFromDisposition(
			response.headers["content-disposition"],
			fallbackFilename,
		),
	};
}

export async function previewRdoPdf(
	constructionId: string,
	rdoId: string,
): Promise<Blob> {
	const response = await handleRequest(() =>
		client.get<Blob>(rdosPath(constructionId, `/${rdoId}/preview`), {
			responseType: "blob",
		}),
	);
	return response.data;
}
